import React, { useState } from 'react';
import { FormatDate } from 'components/dates/FormatDate';
import StatusInfoTab from 'components/tabs/StatusInfoTab';
import SearchBar from 'components/search/SearchBar';
import useTables from '../hooks/useTables';
import { NButton, NTable } from '@innersource-nn/novoui-react';
import {
    DatasetSortField,
    SearchFilter,
    SortDirection,
    TableSortField,
} from 'types/Filters';
import { DatasetTable } from 'types/Dataset';
import { useModal } from 'hooks/useModal';
import TableActionButton from 'components/buttons/TableActionButton';
import PaginationComponent from 'components/pager/Pagination';
import { TableSortStateType, TableSortState } from '@novonordisk/novoui-vuejs';

const tableHeaders = [
    {
        header: 'Name',
        key: 'label',
        isSortable: true,
        initialSortState: TableSortState.desc,
    },
    { header: 'Database Name', key: 'GlueDatabaseName' },
    { header: 'Created', key: 'created', isSortable: true },
    { header: 'Actions', key: 'actions' },
];

type TableProps = {
    enableCrawler: boolean;
    datasetLabel: string;
};
const TableList = ({ enableCrawler, datasetLabel }: TableProps) => {
    const [filter, setFilter] = useState<SearchFilter>({
        term: '',
        page: 1,
        pageSize: 10,
        sort: [
            {
                field: 'label' as TableSortField,
                direction: 'asc' as SortDirection,
            },
        ],
    });
    const {
        tables,
        items,
        error,
        loading,
        showSearchbar,
        synchronizeTables,
        startCrawlingDataset,
        crawlerLoading,
    } = useTables(filter);

    const { openTableAccess } = useModal();

    const sortItems = (newSortMap: Map<string, TableSortStateType>) => {
        for (const [key, value] of newSortMap) {
            const field = key as DatasetSortField;

            if (value === TableSortState.asc || value === TableSortState.desc) {
                setFilter({
                    ...filter,
                    sort: [{ field, direction: value }],
                });
                break; // NTable only allows to sort one field at the time
            }
        }
    };

    const adjustItems = (tables: DatasetTable[]) => {
        return tables.map((table) => {
            return {
                ...table,
                created: FormatDate(table.created),
                actions: (
                    <div className="flex gap-8">
                        <NButton
                            size="tiny"
                            variant="outline"
                            square
                            leftIcon="key"
                            onClick={() =>
                                openTableAccess({
                                    resourceUri: table.tableUri,
                                    label: table.name,
                                    datasetLabel,
                                })
                            }
                        >
                            Request access
                        </NButton>
                        <TableActionButton path={`/table/${table.tableUri}`} />
                    </div>
                ),
            };
        });
    };
    const showTable = !error && (loading || showSearchbar);

    return (
        <div>
            {showTable ? (
                <div>
                    <div className="grid grid-cols-2 mb-24">
                        <div className="flex gap-8">
                            <NButton
                                isLoading={loading}
                                onClick={synchronizeTables}
                                variant="outline"
                                leftIcon="refresh"
                                square
                                size="sm"
                            >
                                Sync Table
                            </NButton>
                            <NButton
                                isLoading={crawlerLoading}
                                onClick={startCrawlingDataset}
                                variant="outline"
                                leftIcon="settingsSelected"
                                square
                                size="sm"
                                disabled={!enableCrawler}
                            >
                                Crawl Dataset
                            </NButton>
                        </div>

                        <SearchBar
                            className="w-full"
                            placeholder="Search for folders and tables..."
                            term={filter.term ?? ''}
                            onTermChange={(e: string) => {
                                setFilter({ ...filter, term: e });
                            }}
                        />
                    </div>
                    <NTable
                        loadXMoreRowsAtATime={loading ? 5 : undefined}
                        headers={tableHeaders}
                        items={loading ? [] : adjustItems(tables)}
                        columnBorder={true}
                        rowKey="tableUri"
                        sorting={sortItems}
                    />
                    <PaginationComponent
                        items={items}
                        filter={filter}
                        setFilter={setFilter}
                    />
                </div>
            ) : (
                <StatusInfoTab child="tables" parent="dataset" error={error} />
            )}
        </div>
    );
};

export default TableList;
